import { MutableRefObject, useRef, useState } from 'react';

import { Avatar, Icon, Modal } from '@components';
import { AccountType } from '@constants/generic';
import useOnClickOutside from '@utils/hooks';
import { Org } from 'types/organization';

import EditOrganization from './EditOrganization';
import OrgAccountList from './OrgAccountList';

import theme from '../../../../themes/colors';

interface OrganisationSwitcherProps {
  currentOrg: any;
  orgList: Org[];
}

const OrganisationSwitcher = ({
  currentOrg,
  orgList
}: OrganisationSwitcherProps) => {
  const [showOrgSwitcherModal, setShowOrgSwitcherModal] =
    useState<boolean>(false);

  const [showOrganizationDetailsModal, setShowOrganizationDetailsModal] =
    useState(false);

  const modalRef = useRef() as MutableRefObject<HTMLDivElement>;

  useOnClickOutside(modalRef, (e) => {
    e.stopPropagation();
    setShowOrgSwitcherModal(false);
  });

  const onCancelModal = () => {
    setShowOrganizationDetailsModal(false);
  };

  return (
    <>
      <div className="relative" ref={modalRef}>
        <div
          className={`group mb-1.5 ml-2 mr-[7px] mt-3 h-10 w-51 cursor-pointer rounded-lg bg-cofeeNeutral1 py-1 pl-3 pr-2 hover:bg-cofeeGreyBlue75 ${
            showOrgSwitcherModal ? 'bg-cofeeGreyBlue75' : ''
          }`}
          role="button"
          onClick={() => setShowOrgSwitcherModal((prev) => !prev)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setShowOrgSwitcherModal((prev) => !prev);
            }
          }}
          tabIndex={0}
        >
          <div className="flex items-center justify-center gap-3 transition-all">
            <div className="flex items-center gap-2 overflow-hidden">
              <Avatar
                name={currentOrg?.name}
                className="size-8 min-w-8 rounded-lg text-theme"
                imageUrl={currentOrg?.image_url}
                bgColor={theme.cofeePurple50}
              />
              <div className="flex w-45 flex-col justify-center truncate  break-words py-1">
                <div className="overflow-hidden text-ellipsis font-medium text-cofeeNeutral10">
                  {currentOrg?.type === AccountType.ORGANIZATION
                    ? currentOrg?.name
                    : currentOrg?.display_name}
                </div>
              </div>
              <div className="flex size-6 min-w-6 cursor-pointer items-center justify-center rounded-full">
                <Icon
                  name="down"
                  stroke={theme.cofeeNeutral8}
                  className={
                    showOrgSwitcherModal
                      ? 'rotate-180 transition-transform duration-300'
                      : 'rotate-0 transition-transform duration-300'
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {showOrgSwitcherModal && (
          <OrgAccountList
            currentOrg={currentOrg}
            orgList={orgList}
            setShowOrgSwitcherModal={setShowOrgSwitcherModal}
            setShowOrganizationDetailsModal={setShowOrganizationDetailsModal}
          />
        )}
      </div>
      {showOrganizationDetailsModal && (
        <Modal isOpen={showOrganizationDetailsModal} onCancel={onCancelModal}>
          <EditOrganization
            imageUrl={currentOrg?.image_url || ''}
            currentOrgName={
              currentOrg?.type === AccountType.INDIVIDUAL
                ? currentOrg?.display_name || ''
                : currentOrg?.name || ''
            }
            currentOrgId={currentOrg?.id || ''}
            handleCancel={onCancelModal}
            accountType={currentOrg?.type || AccountType.ORGANIZATION}
          />
        </Modal>
      )}
    </>
  );
};

export default OrganisationSwitcher;
